import type { FeatureAccess, FeatureAccessKey } from '@unipostudio/platform-constants';
import axios from 'axios';
import { api } from './api';

/**
 * A plan an account can be on (ADR-007): the free Starter plan, Classic
 * (no longer sold, kept by grandfathered subscribers), Pro and Agency.
 */
export type PlanId = 'starter' | 'classic' | 'pro' | 'agency';

/** A plan that can be bought today: Pro or Agency. Classic is not sold. */
export type SellablePlanId = 'pro' | 'agency';

/** Every plan, in ladder order. */
export const PLAN_IDS: readonly PlanId[] = ['starter', 'classic', 'pro', 'agency'];

/** The plans sold, in ladder order. */
export const SELLABLE_PLAN_IDS: readonly SellablePlanId[] = ['pro', 'agency'];

/** Whether a plan id is one that is sold today. */
export function isSellablePlanId(id: string | null | undefined): id is SellablePlanId {
  return id === 'pro' || id === 'agency';
}

/** A plan id from the API's uppercase enum, or null for anything this app does not know. */
export function toPlanId(value: string | null | undefined): PlanId | null {
  const id = (value ?? '').toLowerCase();
  return (PLAN_IDS as readonly string[]).includes(id) ? (id as PlanId) : null;
}

/**
 * The plan an account is on, from the API's enum: anything not paid for
 * (no plan, the retired LAPSED value, an unknown name) is the free Starter
 * plan (ADR-007).
 */
export function toPlanState(value: string | null | undefined): PlanId {
  return toPlanId(value) ?? 'starter';
}

/** One plan bullet as data (GET /subscriptions/plans `featureItems`). */
export interface PlanFeatureItem {
  key: string;
  values?: Record<string, number | string>;
}

/** A price pair, in the plan's currency. */
export interface PricePair {
  monthly: number;
  yearly: number;
}

export interface Plan {
  id: PlanId;
  name: string;
  priceMonthly: number;
  priceYearly: number;
  /** The currency this workspace is charged in, which follows its billing country. */
  currency: string;
  /** False: shown for information, but no provider charges this currency yet. Always false for Starter. */
  purchasable?: boolean;
  /** The free Starter plan (ADR-007): nothing to buy, every account has it. */
  free?: boolean;
  /** The price already includes tax: rupee prices include 18% GST (ADR-007). */
  taxIncluded?: boolean;
  /**
   * Which intervals can actually be bought (GET /subscriptions/plans,
   * BILLING.md §14): a yearly rupee price with no yearly Razorpay plan is
   * shown for information only. Absent from an older API: `purchasable` decides.
   */
  purchasableIntervals?: { monthly: boolean; yearly: boolean };
  /** Brands the plan includes (1 / 3 / 5). Absent from an older API. */
  includedBrands?: number;
  /** Agency: the price of each brand above the included ones, in `currency`. */
  extraBrandPrice?: PricePair | null;
  /** English bullets from the API; shown only when `featureItems` is absent (older API). */
  features: string[];
  /** The bullets as keys under settings.billingTab.planFeatures, with their values. */
  featureItems?: PlanFeatureItem[];
  limits: {
    platforms: number;
    postsPerMonth: number | null;
    teamSeats: number;
    aiGenerations: number | null;
    storageMb: number;
    analyticsDays: number;
  };
}

/**
 * Usage counters from GET /subscriptions/me, each read from what its gate
 * counts. A limit of -1 is unlimited, 0 a feature the plan lacks. The AI
 * counters stay optional so an older API that sends none shows no bar
 * rather than an invented one.
 */
export interface SubscriptionUsage {
  postsUsed: number;
  postsLimit: number;
  aiRepliesUsed?: number;
  aiRepliesLimit?: number;
  /** X post credits this month (a post 1, a post with a link 13); -1 unlimited. */
  xPostCreditsUsed?: number;
  xPostCreditsLimit?: number;
  aiGenerationsUsed?: number;
  aiGenerationsLimit?: number;
  /** Channels one post may go to (platforms.connect, overrides counted); -1 unlimited. */
  platformsLimit?: number;
}

/**
 * How many brands the account holds and may hold, from GET /subscriptions/me
 * (ADR-006). `limit` is included + paid extras; `extra` is the Agency extra
 * brands paid for.
 */
export interface BrandUsage {
  used: number;
  included: number;
  extra: number;
  limit: number;
}

export interface Subscription {
  id: string;
  /**
   * The plan written on the account's subscription: the last plan it had,
   * even when it has ended. Read the plan in force through useAccountState(),
   * never this.
   */
  planId: PlanId;
  /**
   * The plan in force (the API's effectivePlan()), when the API sends it.
   * Starter for an account that is not paying.
   */
  effectivePlanId: PlanId | null;
  /** 'trialing' is an admin's timed comp: no sign-up gets a trial (ADR-007). */
  status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'expired';
  /**
   * When a paid plan that will not renew falls back to Starter; null when it
   * renews or the account is on Starter already.
   */
  planEndsAt: string | null;
  /** On Classic, which is no longer sold: it keeps its price until it changes plan (ADR-007). */
  grandfathered: boolean;
  /** Agency extra brands paid for. */
  extraBrands: number;
  /** Brands used and allowed, when the API sends them. */
  brands: BrandUsage | null;
  /** 'monthly' or 'yearly', once a plan is paid for. */
  billingInterval: string | null;
  /** The interval of the open, unpaid checkout, when the API records it. */
  pendingInterval: 'monthly' | 'yearly' | null;
  /** The paid period's dates; null when the provider has not set them (a comp, a fresh checkout). */
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  razorpaySubscriptionId: string | null;
  /** The currency the plan in force is billed in, when one is billed. */
  currency: string | null;
  /**
   * A checkout opened and not yet paid. The plan in force is still planId:
   * nothing changes until the provider confirms payment.
   */
  pendingPlanId: PlanId | null;
  /** This month's usage. postsLimit === -1 means unlimited. */
  usage: SubscriptionUsage | null;
  /**
   * Flag-gated features the account has or lacks, override-aware, and the
   * plan that has each (FEATURE_ACCESS_KEYS). Null from an older API.
   */
  access?: Partial<Record<FeatureAccessKey, FeatureAccess>> | null;
  /** Granted by UniPostudio, nothing billed (AL-05): no renewal to cancel, no charge date. */
  comped?: boolean;
  /** Who bills it: razorpay, paddle, or admin (a comp). */
  provider?: string | null;
  /** What the plan paused because the account holds more than it allows (D-01). */
  paused?: { brands: number; channels: number; members: number };
}

/** Who is invoiced, and the country that decides the currency. GET/PUT /billing/identity. */
export interface BillingIdentity {
  legalName: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  vatNumber: string | null;
  /** True only when VIES confirmed the number. */
  vatValidated: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  region: string | null;
  /** India: a registered buyer's GSTIN, printed on tax invoices (AL-03). */
  gstin: string | null;
  /** India: ISO 3166-2 code of the state ("IN-KL"), the place of supply. */
  stateCode: string | null;
  /** Where invoices and receipts are also emailed. */
  billingEmail: string | null;
  updatedAt: string;
}

export type BillingIdentityInput = Omit<BillingIdentity, 'vatValidated' | 'updatedAt'>;

/** The raw /subscriptions/me row, before it is flattened for the app. */
interface SubscriptionRow {
  id: string;
  plan: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd?: boolean;
  razorpaySubscriptionId?: string | null;
  currency?: string | null;
  pendingPlan?: string | null;
  extraBrands?: number | null;
  billingInterval?: string | null;
  pendingInterval?: string | null;
  brands?: RawBrandUsage | null;
  provider?: string | null;
}

type RawBrandUsage = Partial<BrandUsage> & { extraBrands?: number };

/** The raw /subscriptions/me body. */
interface SubscriptionBody {
  subscription?: SubscriptionRow | null;
  /** effectivePlan() of the account. */
  plan?: string | null;
  planEndsAt?: string | null;
  grandfathered?: boolean;
  usage?: SubscriptionUsage;
  brands?: RawBrandUsage | null;
  access?: Partial<Record<FeatureAccessKey, FeatureAccess>>;
  comped?: boolean;
  paused?: { brands: number; channels: number; members: number };
}

/** The raw plan row from /subscriptions/plans, tolerant of the field names the API may use. */
interface PlanRow extends Omit<Plan, 'id' | 'extraBrandPrice' | 'includedBrands' | 'limits'> {
  id: string;
  limits: Plan['limits'] & { brands?: number };
  /** What the API sends for Agency (ADR-006): included brands and the price of each further one. */
  extraBrand?: { included: number; priceMonthly: number; priceYearly: number } | null;
  includedBrands?: number;
  maxBrands?: number;
  extraBrandPrice?: PricePair | number | null;
  extraBrandPriceMonthly?: number;
  extraBrandPriceYearly?: number;
}

/** The plans endpoint answers an array, or `{ plans, extraBrandPrice }`. */
type PlansBody = PlanRow[] | { plans: PlanRow[]; extraBrandPrice?: PricePair | null };

function toBrandUsage(raw: RawBrandUsage | null | undefined): BrandUsage | null {
  if (!raw || typeof raw.used !== 'number' || typeof raw.limit !== 'number') return null;
  return {
    used: raw.used,
    limit: raw.limit,
    included: typeof raw.included === 'number' ? raw.included : raw.limit,
    extra: typeof raw.extra === 'number' ? raw.extra : typeof raw.extraBrands === 'number' ? raw.extraBrands : 0,
  };
}

function toPricePair(raw: PricePair | number | null | undefined, yearly?: number): PricePair | null {
  if (raw && typeof raw === 'object' && typeof raw.monthly === 'number') return { monthly: raw.monthly, yearly: raw.yearly };
  if (typeof raw === 'number') return { monthly: raw, yearly: yearly ?? raw * 12 };
  return null;
}

/**
 * Whether a plan can be bought on an interval: the API's per-interval flag
 * when it sends one (BILLING.md §14), else the plan's single flag.
 */
export function intervalBuyable(plan: Plan | undefined, interval: 'monthly' | 'yearly'): boolean {
  if (!plan) return true;
  if (plan.purchasableIntervals) return plan.purchasableIntervals[interval];
  return plan.purchasable !== false;
}

/**
 * Normalises the plans body into the plans listed (ADR-007: Starter, Pro,
 * Agency), in ladder order. Classic is never offered, so a row naming it is
 * dropped even from an older API.
 */
export function toPlans(body: PlansBody | null | undefined): Plan[] {
  const rows = Array.isArray(body) ? body : body?.plans ?? [];
  const sharedExtra = Array.isArray(body) ? null : toPricePair(body?.extraBrandPrice ?? null);
  const plans: Plan[] = [];
  for (const row of rows) {
    const id = toPlanId(row.id);
    if (!id || id === 'classic') continue;
    const extra =
      (row.extraBrand ? { monthly: row.extraBrand.priceMonthly, yearly: row.extraBrand.priceYearly } : null) ??
      toPricePair(row.extraBrandPrice ?? row.extraBrandPriceMonthly ?? null, row.extraBrandPriceYearly) ??
      (id === 'agency' ? sharedExtra : null);
    const included = row.extraBrand?.included ?? row.includedBrands ?? row.maxBrands ?? row.limits?.brands;
    plans.push({
      id,
      name: row.name,
      priceMonthly: row.priceMonthly,
      priceYearly: row.priceYearly,
      currency: row.currency,
      ...(row.purchasable === undefined ? {} : { purchasable: row.purchasable }),
      ...(id === 'starter' || row.free === true ? { free: true } : {}),
      ...(row.taxIncluded === undefined ? {} : { taxIncluded: row.taxIncluded }),
      ...(row.purchasableIntervals ? { purchasableIntervals: row.purchasableIntervals } : {}),
      ...(included === undefined ? {} : { includedBrands: included }),
      extraBrandPrice: extra,
      features: row.features ?? [],
      ...(Array.isArray(row.featureItems) ? { featureItems: row.featureItems } : {}),
      limits: row.limits,
    });
  }
  return plans.sort((a, b) => PLAN_IDS.indexOf(a.id) - PLAN_IDS.indexOf(b.id));
}

/** The second Razorpay subscription an Agency checkout needs for brands above five. */
export interface ExtraBrandAuthorization {
  provider: 'razorpay';
  subscriptionId: string;
  shortUrl: string | null;
  razorpayKeyId: string;
  /** Extra brands billed. */
  quantity: number;
  /** Rupees per brand per interval. */
  amount: number;
  currency: string;
  interval: 'monthly' | 'yearly';
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  paidAt: string | null;
  invoiceUrl: string | null;
  createdAt: string;
  /** Who charged it: Razorpay (rupees) or Paddle (euro, dollars). */
  provider?: 'razorpay' | 'paddle';
  /** A charge, or a GST credit note for a refund. */
  kind?: 'charge' | 'credit_note';
  /** Our GST tax invoice or credit note for it (rupee charges, AL-02). */
  taxInvoice?: TaxInvoiceRef | null;
}

/** The GST document of a row of the charge history. */
export interface TaxInvoiceRef {
  id: string;
  kind: 'invoice' | 'credit_note';
  /** pending: recorded, waiting for its number. */
  status: 'pending' | 'issued';
  number: string | null;
  pdfReady: boolean;
}

export type RefundStatus = 'requested' | 'approved' | 'refunded' | 'declined';

/** A charge with its refund state (Task 4.2). GET /billing/refund-requests. */
export interface RefundableInvoice extends Invoice {
  refund: { id: string; status: RefundStatus } | null;
  /** Null when a refund can be requested; otherwise why not (REFUNDS_OFF, TOO_OLD, YEARLY_LIMIT, ...). */
  refundBlockedBy: string | null;
}

export interface RefundRequests {
  /** billing.refund_request: on for every plan since 2026-10-26; the admin console can turn it off. */
  enabled: boolean;
  windowDays: number;
  invoices: RefundableInvoice[];
}

/**
 * What the server returns to open a plan checkout. Which one depends on the
 * workspace's currency: Razorpay for rupees, Paddle for euro and dollars.
 */
export type CheckoutResponse =
  | {
      provider: 'razorpay';
      /** A plan is a recurring SUBSCRIPTION, opened via subscription_id. */
      subscriptionId: string;
      shortUrl: string;
      razorpayKeyId: string;
      amount: number;
      currency: string;
      interval?: 'monthly' | 'yearly';
      /**
       * Agency for an account holding more than five brands, in rupees: the
       * extras are a second Razorpay subscription, authorised after the plan.
       */
      extraBrandAuthorization?: ExtraBrandAuthorization;
    }
  | {
      provider: 'paddle';
      transactionId: string;
      clientToken: string;
      environment: 'sandbox' | 'production';
      amount: number;
      currency: string;
    };

/** GET /subscriptions/me/next-charge: what the next renewal charges, tax included (Task 4.1). */
export interface NextCharge {
  amountMinor: number;
  currency: string;
  at: string;
  taxIncluded: true;
  /** Razorpay bills Agency's extra brands on their own date (AL-51). */
  extras?: { amountMinor: number; currency: string; at: string; quantity: number } | null;
}

/** GET /subscriptions/change-quote: what a plan change does before it is made (AL-24). */
export interface ChangeQuote {
  plan: string;
  interval: 'monthly' | 'yearly';
  currency: string;
  taxIncluded: boolean;
  chargeNow: number;
  refundEstimate: number;
  effective: 'now';
  losing: { key: string; from: number | null; to: number | null }[];
  brandsPaused: number;
}

export const billingService = {
  async getChangeQuote(planId: SellablePlanId, interval: 'monthly' | 'yearly'): Promise<ChangeQuote> {
    const res = await api.get<ChangeQuote>('/subscriptions/change-quote', { params: { plan: planId.toUpperCase(), interval } });
    return res.data;
  },

  /** The provider's page where the card or UPI mandate is updated (M-03). */
  async getPaymentMethodLink(): Promise<{ provider: 'razorpay' | 'paddle'; url: string }> {
    const res = await api.get<{ provider: 'razorpay' | 'paddle'; url: string }>('/subscriptions/payment-method-link');
    return res.data;
  },

  /** Asks for a plan that cannot be bought in the account's currency yet (M-11). */
  async requestUpgrade(planId: SellablePlanId, interval: 'monthly' | 'yearly'): Promise<{ requested: true }> {
    const res = await api.post<{ requested: true }>('/billing/upgrade-requests', { plan: planId.toUpperCase(), interval });
    return res.data;
  },

  /** The next renewal, or null when nothing renews (Starter, a cancelled plan). */
  async getNextCharge(): Promise<NextCharge | null> {
    const res = await api.get<NextCharge | null>('/subscriptions/me/next-charge');
    return res.data ?? null;
  },

  async getPlans(): Promise<Plan[]> {
    const res = await api.get<PlansBody>('/subscriptions/plans');
    return toPlans(res.data);
  },

  /**
   * The subscription in force, or null when the workspace has none yet.
   *
   * Only a 404 means "none". Any other failure is thrown: answering null
   * to a network error would show the free plan with every paid plan
   * offered as an upgrade to someone who is already paying.
   */
  async getCurrentSubscription(): Promise<Subscription | null> {
    let data: SubscriptionBody | undefined;
    try {
      // Backend returns { subscription, features, usage, brands } with
      // subscription.plan as uppercase enum (STARTER/CLASSIC/PRO/AGENCY).
      // Flatten + lowercase here so the frontend keeps a stable shape. The
      // subscription is the account's: every brand of the account shares it.
      const res = await api.get<SubscriptionBody>('/subscriptions/me');
      data = res.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
    const inner = data?.subscription;
    if (!inner) return null;
    return {
      id: inner.id,
      planId: toPlanState(inner.plan),
      effectivePlanId: data?.plan ? toPlanState(data.plan) : null,
      status: inner.status as Subscription['status'],
      planEndsAt: data?.planEndsAt ?? null,
      grandfathered: data?.grandfathered === true,
      extraBrands: inner.extraBrands ?? 0,
      brands: toBrandUsage(data?.brands ?? inner.brands),
      billingInterval: inner.billingInterval ?? null,
      pendingInterval: inner.pendingInterval === 'yearly' || inner.pendingInterval === 'monthly' ? inner.pendingInterval : null,
      currentPeriodStart: inner.currentPeriodStart || null,
      currentPeriodEnd: inner.currentPeriodEnd || null,
      // A cancelled renewal runs to the end of its period (AL-52): the API
      // records it as status `cancelled`, not as a flag.
      cancelAtPeriodEnd: !!inner.cancelAtPeriodEnd || ['cancelled', 'canceled'].includes(String(inner.status).toLowerCase()),
      razorpaySubscriptionId: inner.razorpaySubscriptionId ?? null,
      currency: inner.currency ?? null,
      pendingPlanId: toPlanId(inner.pendingPlan),
      usage: data?.usage ?? null,
      access: data?.access ?? null,
      comped: data?.comped === true || String(inner.status).toLowerCase() === 'trialing',
      provider: inner.provider ?? null,
      paused: data?.paused ?? { brands: 0, channels: 0, members: 0 },
    };
  },

  /** Drops an open, unpaid checkout. The plan in force is untouched. */
  async dismissPendingCheckout(): Promise<{ dismissed: boolean }> {
    const res = await api.delete<{ dismissed: boolean }>('/subscriptions/pending');
    return res.data;
  },

  /** The billing identity, or null when none has been saved. */
  async getIdentity(): Promise<BillingIdentity | null> {
    const res = await api.get<BillingIdentity | null>('/billing/identity');
    return res.data ?? null;
  },

  async saveIdentity(input: BillingIdentityInput): Promise<BillingIdentity> {
    const res = await api.put<BillingIdentity>('/billing/identity', input);
    return res.data;
  },

  async getInvoices(): Promise<Invoice[]> {
    const res = await api.get<Invoice[]>('/subscriptions/invoices');
    return res.data;
  },

  /** A five-minute link to a GST invoice's or credit note's PDF (409 INVOICE_PDF_PENDING while it is made). */
  async getInvoicePdf(id: string): Promise<{ url: string; filename: string }> {
    const res = await api.get<{ url: string; filename: string }>(`/billing/invoices/${encodeURIComponent(id)}/pdf`);
    return res.data;
  },

  /** Paddle's own invoice PDF for a euro or dollar charge (a link Paddle keeps for about an hour). */
  async getPaddleInvoicePdf(transactionId: string): Promise<{ url: string }> {
    const res = await api.get<{ url: string }>(`/billing/invoices/paddle/${encodeURIComponent(transactionId)}/pdf`);
    return res.data;
  },

  async getRefundRequests(): Promise<RefundRequests> {
    const res = await api.get<RefundRequests>('/billing/refund-requests');
    return res.data;
  },

  async requestRefund(invoiceId: string, reason?: string): Promise<{ id: string; status: RefundStatus }> {
    const res = await api.post<{ id: string; status: RefundStatus }>('/billing/refund-requests', reason ? { invoiceId, reason } : { invoiceId });
    return res.data;
  },

  async createCheckout(planId: SellablePlanId, interval: 'monthly' | 'yearly'): Promise<CheckoutResponse> {
    const res = await api.post('/subscriptions/create', { plan: planId, interval });
    return res.data;
  },

  /**
   * Confirms a Razorpay payment. The API resolves the account from
   * X-Workspace-Id, so a payment for another account than the brand in view
   * (the add-brand flow's own account) names one of that account's brands.
   */
  async confirmPayment(
    razorpayPaymentId: string,
    razorpaySignature: string,
    subscriptionId: string,
    options: { workspaceId?: string } = {},
  ): Promise<Subscription> {
    const res = await api.post(
      '/subscriptions/confirm',
      {
        razorpayPaymentId,
        razorpaySignature,
        razorpaySubscriptionId: subscriptionId,
      },
      options.workspaceId ? { headers: { 'X-Workspace-Id': options.workspaceId } } : undefined,
    );
    return res.data;
  },

  /**
   * Agency owner: quote room for `quantity` more brands ahead of time
   * (POST /subscriptions/extra-brands). Nothing is bought by a quote.
   */
  async quoteBrandRoom(quantity: number): Promise<unknown> {
    // Answers 403 EXTRA_BRAND_REQUIRED with the quote and `confirmWith`.
    const res = await api.post('/subscriptions/extra-brands', { quantity }, { handlesPlanErrors: true });
    return res.data;
  },

  /**
   * Buys the room exactly as quoted: the body is the quote's `confirmWith`
   * (`{ extraBrandsAfter: T, accept: true }`, BILLING.md §13), sent verbatim.
   * It raises the paid extras TO T, so it cannot add a second brand.
   */
  async buyBrandRoom(confirmWith: Record<string, unknown>): Promise<unknown> {
    const res = await api.post('/subscriptions/extra-brands', confirmWith, { handlesPlanErrors: true });
    return res.data;
  },

  async cancelSubscription(): Promise<Subscription> {
    const res = await api.delete('/subscriptions');
    return res.data;
  },
};
